import React, { useState, useMemo } from "react";
import {
  Box,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  Button,
  Stack,
  Icon,
  Alert,
  ListSubheader,
  InputAdornment,
} from "investira.react.components";
import { validators } from "investira.sdk";
import { DecimalTextField } from "../../molecules";

/**
 * Gera uma lista de opcoes normalizada a partir de opcoes ou opcoesGrouped
 */
const normalizarOpcoes = (pOpcoes, pOpcoesGrouped) => {
  // opcoesGrouped: tipo { [grupo]: [array] }
  // Gera cada grupo como { grupo, items: [array] }
  if (
    pOpcoesGrouped &&
    typeof pOpcoesGrouped === "object" &&
    Object.keys(pOpcoesGrouped).length > 0
  ) {
    return Object.entries(pOpcoesGrouped).reduce((acc, [xGrupo, xArr]) => {
      if (
        xArr &&
        Array.isArray(xArr) &&
        xArr.length > 0 &&
        typeof xArr[0] === "object"
      ) {
        acc.push({ grupo: xGrupo, items: xArr });
      }
      return acc;
    }, []);
  }
  // caso array plano de strings
  if (Array.isArray(pOpcoes) && pOpcoes.length > 0) {
    return [{ grupo: null, items: pOpcoes }];
  }
  // caso array de objetos, sem agrupar
  if (
    Array.isArray(pOpcoes) &&
    pOpcoes.length > 0 &&
    typeof pOpcoes[0] === "object"
  ) {
    return [{ grupo: null, items: pOpcoes }];
  }
  return [];
};

/**
 * Extrai o valor de chave a ser salvo, padronizando para string ("indicador_id" ou valor string)
 */
const getChaveNormalizada = (xOpcao) => {
  if (typeof xOpcao === "string") return xOpcao;
  if (typeof xOpcao === "object" && xOpcao && xOpcao.indicador_id)
    return xOpcao.indicador_id;
  // fallback genérico
  if (xOpcao && xOpcao.value) return xOpcao.value;
  return "";
};

/**
 * Obtém o label a exibir para a opção.
 * TODO: Generalizar
 */
const getLabelNormalizada = (xOpcao) => {
  if (typeof xOpcao === "string") return xOpcao;
  if (typeof xOpcao === "object" && xOpcao && xOpcao.indicador_seuid)
    return xOpcao.indicador_seuid;
  // fallback para "benchmark" caso exista
  if (xOpcao && xOpcao.benchmark) return xOpcao.benchmark;
  // fallback genérico
  if (xOpcao && xOpcao.label) return xOpcao.label;
  if (xOpcao && xOpcao.value) return xOpcao.value;
  return "";
};

const ObjectBuilder = (pProps) => {
  const {
    groups,
    opcoes = [],
    opcoesGrouped = {},
    label,
    btnAddLabel = "Adicionar Linha",
    chaveLabel = "Chave",
    valorLabel = "Valor",
    mensagemVazio = null,
    initialValues,
    onChange,
  } = pProps;

  // Estados devem ser prefixados por x (nomeacao-variaveis)
  const [xLinhas, setXLinhas] = useState([]);
  const [xObjeto, setXObjeto] = useState({});

  const xInitialValues = useMemo(() => initialValues, [initialValues]);

  // Normalizar as opções em estrutura agrupada para renderização
  const xOpcoesNormalizadas = useMemo(
    () => normalizarOpcoes(opcoes, opcoesGrouped),
    [opcoes, opcoesGrouped]
  );

  // Lista compacta só de chaves já selecionadas exceto a do próprio index
  const xChavesSelecionadas = (pIndexAtual) =>
    xLinhas
      .map((xLinha, xIndex) => (xIndex !== pIndexAtual ? xLinha.chave : null))
      .filter(Boolean);

  // Para uso no length/limite de botões
  const xTotalOpcoes = useMemo(() => {
    return xOpcoesNormalizadas.reduce(
      (acc, group) => acc + (group.items ? group.items.length : 0),
      0
    );
  }, [xOpcoesNormalizadas]);

  // Filtra para não repetir
  const xOpcoesDisponiveisFiltradas = (pIndexAtual) => {
    const xUsadas = xChavesSelecionadas(pIndexAtual);
    let xDisponiveis = [];
    xOpcoesNormalizadas.forEach(({ grupo, items }) => {
      // Mantém a estrutura dos grupos, mas filtra cada item
      const xGroupItems = items.filter(
        (xItem) => !xUsadas.includes(getChaveNormalizada(xItem))
      );
      xDisponiveis.push({ grupo, items: xGroupItems });
    });
    return xDisponiveis;
  };

  // Adiciona nova linha
  const adicionarLinha = () => {
    setXLinhas([...xLinhas, { chave: "", valor: "" }]);
  };

  // Remove linha e atualiza objeto
  const removerLinha = (pIndex) => {
    const xNovasLinhas = xLinhas.filter((_, i) => i !== pIndex);
    setXLinhas(xNovasLinhas);

    const xNovoObjeto = {};
    xNovasLinhas.forEach((xLinha) => {
      if (xLinha.chave && xLinha.valor) {
        xNovoObjeto[xLinha.chave] = parseFloat(xLinha.valor);
      }
    });
    setXObjeto(xNovoObjeto);
    onChange && onChange(xNovoObjeto);
  };

  // Atualiza chave de uma linha, removendo anterior e atribuindo nova
  const atualizarChave = (pIndex, pNovaChave) => {
    const xNovasLinhas = [...xLinhas];
    const xChaveAnterior = xNovasLinhas[pIndex].chave;
    xNovasLinhas[pIndex].chave = pNovaChave;
    setXLinhas(xNovasLinhas);

    const xNovoObjeto = { ...xObjeto };
    if (xChaveAnterior && xNovoObjeto.hasOwnProperty(xChaveAnterior)) {
      delete xNovoObjeto[xChaveAnterior];
    }
    if (pNovaChave && xNovasLinhas[pIndex].valor) {
      xNovoObjeto[pNovaChave] = parseFloat(xNovasLinhas[pIndex].valor);
    }
    setXObjeto(xNovoObjeto);
    onChange && onChange(xNovoObjeto);
  };

  const atualizarValor = (pIndex, pNovoValor) => {
    // Validar input para aceitar apenas números com até 2 casas decimais
    // Permite "-" no início para valores negativos (gerenciado pelo DecimalTextField)
    const xRegex = /^-?\d*\.?\d{0,2}$/;
    // Também permite apenas "-" enquanto o usuário está digitando
    if (pNovoValor === "" || pNovoValor === "-" || xRegex.test(pNovoValor)) {
      const xNovasLinhas = [...xLinhas];
      xNovasLinhas[pIndex].valor = pNovoValor;
      setXLinhas(xNovasLinhas);

      if (xNovasLinhas[pIndex].chave && pNovoValor && pNovoValor !== "-") {
        const xNovoObjeto = { ...xObjeto };
        xNovoObjeto[xNovasLinhas[pIndex].chave] = parseFloat(pNovoValor);
        setXObjeto(xNovoObjeto);
        onChange && onChange(xNovoObjeto);
      }
    }
  };

  // Inicializa xLinhas e xObjeto com base no initialValues, se existir
  React.useEffect(() => {
    if (
      xInitialValues &&
      typeof xInitialValues === "object" &&
      Object.keys(xInitialValues).length > 0
    ) {
      const xNovasLinhas = Object.entries(xInitialValues).map(
        ([xChave, xValor]) => ({
          chave: xChave,
          valor: xValor !== undefined && xValor !== null ? String(xValor) : "",
        })
      );
      setXLinhas(xNovasLinhas);
      setXObjeto(xInitialValues);
    }
  }, []);

  return (
    <Stack flexGrow={1}>
      <Stack sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="textSecondary" gutterBottom>
          {label}
        </Typography>
      </Stack>

      <Stack spacing={2} sx={{ flexGrow: 1, mb: 3 }}>
        {xLinhas.length === 0 && !validators.isEmpty(mensagemVazio) ? (
          <Alert severity="info">{mensagemVazio}</Alert>
        ) : (
          xLinhas.map((xLinha, xIndex) => (
            <Box
              key={xIndex}
              sx={{
                display: "flex",
                flexGrow: 1,
                gap: 2,
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexGrow: 1,
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Stack flex={1} sx={{ maxWidth: "50%" }}>
                  <TextField
                    select
                    variant="outlined"
                    label={chaveLabel}
                    value={xLinha.chave}
                    onChange={(e) => atualizarChave(xIndex, e.target.value)}
                  >
                    {xOpcoesDisponiveisFiltradas(xIndex).map(
                      ({ grupo, items }) =>
                        grupo
                          ? [
                              <ListSubheader key={`g-${grupo}`}>
                                {groups[grupo].descricao}
                              </ListSubheader>,
                              ...items.map((xOpcao) => {
                                const xChave = getChaveNormalizada(xOpcao);
                                return (
                                  <MenuItem key={xChave} value={xChave}>
                                    {getLabelNormalizada(xOpcao)}
                                  </MenuItem>
                                );
                              }),
                            ]
                          : items.map((xOpcao) => {
                              const xChave = getChaveNormalizada(xOpcao);
                              return (
                                <MenuItem key={xChave} value={xChave}>
                                  {getLabelNormalizada(xOpcao)}
                                </MenuItem>
                              );
                            })
                    )}
                  </TextField>
                </Stack>
                <Stack flex={1} sx={{ maxWidth: "50%" }}>
                  <DecimalTextField
                    id="valor"
                    name="valor"
                    label={valorLabel}
                    variant="outlined"
                    value={xLinha.valor}
                    placeholder={"0,00"}
                    onChange={(_, pValue) => atualizarValor(xIndex, pValue)}
                    allowNegative={true}
                    decimal={2}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton color="error" onClick={() => removerLinha(xIndex)}>
                  <Icon iconName="remove" color="error" size={21} />
                </IconButton>
              </Box>
            </Box>
          ))
        )}
      </Stack>

      <Button
        variant="contained"
        startIcon={<Icon iconName="add" color="inherit" size={16} />}
        onClick={adicionarLinha}
        disabled={xLinhas.length >= xTotalOpcoes}
        size="small"
      >
        {btnAddLabel}
      </Button>

      {/* 
        <Typography variant="h6" gutterBottom>
          Objeto:
        </Typography>
        <pre style={{ margin: 0, overflow: "auto" }}>
          {JSON.stringify({ xObjeto }, null, 2)}
        </pre>
       */}
    </Stack>
  );
};

export default ObjectBuilder;
